import React from 'react';
import { Individual as ABoxIndividual, KnowledgeGraph } from '@semantical/modules/knowledge-graph';
import { SchemaClass, RestrictedObjectProperty, computeRestrictedObjectProperties, classExpressionToJSON } from '@semantical/modules/ontology-model';
import { DefinitionGenerator } from '@semantical/modules/knowledge-graph';
import type { Node, Individual } from '@semantical/modules/semantical-core';
import { isIndividual } from '@semantical/modules/semantical-core';
import EditableDataProperty from './EditableDataProperty';
import EditableObjectProperty from './EditableObjectProperty';
import HasValueChip from './HasValueChip';
import {
    isRequiredDataProperty,
    isOptionalDataProperty,
    isRequiredObjectPropertyRestriction,
    isOptionalObjectPropertyRestriction
} from './utils';

type State = {
    focusedInput: string | null;
    refreshKey: number;
    showRestrictionsDebug: boolean;
    nameEditing: boolean;
    optionalPropertiesExpanded: boolean;
}

type Props = {
    modelElement: Node | null
    onDataPropertyValueChange?: (individual: ABoxIndividual, propertyUri: string, value: string | null) => void
    knowledgeGraph?: KnowledgeGraph
    onObjectPropertyValueChange?: (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetIndividualUri: string | null) => void
    onObjectPropertyValueRemove?: (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetIndividualUri: string) => void
    onExportEssentialTTL?: (ttl: string, individualName: string) => void
    onNameChange?: (element: Node, newName: string) => void
    /** Get individuals by class URI from workspace (all loaded ontologies) */
    getIndividualsByClass?: (classUri: string) => Individual[]
    /** Get individual by URI from workspace (all loaded ontologies) */
    getIndividualByUri?: (uri: string) => Individual | undefined
}

class Properties extends React.Component<Props, State> {

    private nameInputRef = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            focusedInput: null,
            refreshKey: 0,
            showRestrictionsDebug: false,
            nameEditing: false,
            optionalPropertiesExpanded: false
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
        // Focus input when entering edit mode
        if (this.state.nameEditing && !prevState.nameEditing && this.nameInputRef.current) {
            this.nameInputRef.current.focus();
            this.nameInputRef.current.select();
        }

        // Reset editing state when element changes
        if (prevProps.modelElement !== this.props.modelElement && this.state.nameEditing) {
            this.setState({ nameEditing: false });
        }
    }

    handleNameClick = () => {
        this.setState({ nameEditing: true });
    };

    handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newName = e.target.value.trim();
        const currentElement = this.props.modelElement;

        if (currentElement && newName && newName !== currentElement.name) {
            this.props.onNameChange?.(currentElement, newName);
        }

        this.setState({ nameEditing: false });
    };

    handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
            this.setState({ nameEditing: false });
        }
    };

    handleFocusChange = (key: string | null) => {
        this.setState({ focusedInput: key });
    }

    handleRefresh = () => {
        this.setState(state => ({ refreshKey: state.refreshKey + 1 }));
    }

    handleDataPropertyValueChange = (individual: ABoxIndividual, propertyUri: string, value: string | null) => {
        if (this.props.onDataPropertyValueChange) {
            this.props.onDataPropertyValueChange(individual, propertyUri, value);
        }
    }

    handleObjectPropertyValueChange = (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetUri: string | null) => {
        if (this.props.onObjectPropertyValueChange) {
            this.props.onObjectPropertyValueChange(individual, restriction, targetUri);
        }
    }

    handleObjectPropertyValueRemove = (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetUri: string) => {
        if (this.props.onObjectPropertyValueRemove) {
            this.props.onObjectPropertyValueRemove(individual, restriction, targetUri);
        }
    }

    handleDeclareOnIndividual = (individual: ABoxIndividual, restriction: RestrictedObjectProperty) => {
        if (this.props.onObjectPropertyValueChange && restriction.hasValue) {
            this.props.onObjectPropertyValueChange(
                individual,
                restriction,
                restriction.hasValue
            );
        }
    }

    handleClearABoxDeclaration = (individual: ABoxIndividual, restriction: RestrictedObjectProperty) => {
        if (this.props.onObjectPropertyValueRemove && restriction.hasValue) {
            this.props.onObjectPropertyValueRemove(individual, restriction, restriction.hasValue);
        }
    }

    handleExportEssentialTTL = (individual: ABoxIndividual, schemaClass: SchemaClass) => {
        if (this.props.onExportEssentialTTL) {
            const generator = new DefinitionGenerator(this.props.knowledgeGraph);
            const ttl = generator.toTurtle(schemaClass, individual);
            this.props.onExportEssentialTTL(ttl, individual.name);
        }
    }

    toggleRestrictionsDebug = () => {
        this.setState(state => ({ showRestrictionsDebug: !state.showRestrictionsDebug }));
    }

    /**
     * Serialize RestrictedObjectProperty array to JSON for debug display.
     * Includes the source axioms (class expressions) that were used to compute each restriction.
     */
    serializeRestrictionsToJSON(restrictions: RestrictedObjectProperty[]): string {
        const serialized = restrictions.map(r => ({
            onProperty: r.onProperty,
            label: r.label,
            cardinality: r.cardinality,
            targetClassUri: r.targetClassUri,
            targetClassLabel: r.targetClassLabel,
            hasValue: r.hasValue,
            hasValueLabel: r.hasValueLabel,
            isUniversal: r.isUniversal,
            sourceAxioms: r.sourceAxioms.map(axiom => classExpressionToJSON(axiom))
        }));
        return JSON.stringify(serialized, null, 2);
    }

    checkHasABoxDeclaration(individual: ABoxIndividual, restriction: RestrictedObjectProperty): boolean {
        return this.props.knowledgeGraph
            ? Array.from(this.props.knowledgeGraph.relationships.values()).some(
                r => r.subject === individual &&
                     r.predicate.uri === restriction.uri &&
                     r.object.uri === restriction.hasValue
            )
            : false;
    }

    renderEssentialPropertiesForIndividual(schemaClass: SchemaClass, individual: ABoxIndividual) {
        const dataProperties = schemaClass.dataProperties || [];
        const objectPropertyRestrictions = computeRestrictedObjectProperties({ subClassOf: schemaClass._subClassOf });

        const mandatoryDataProps = dataProperties.filter(isRequiredDataProperty);
        const fullyConstrainedDataUris = new Set(
            mandatoryDataProps
                .filter(p => {
                    if (!p.cardinality) return false;
                    const min = p.cardinality.min ?? (p.cardinality.exact !== undefined ? p.cardinality.exact : 0);
                    const max = p.cardinality.max ?? p.cardinality.exact;
                    return min >= 1 && max === 1;
                })
                .map(p => p.uri)
        );
        const optionalDataProps = dataProperties.filter(p =>
            isOptionalDataProperty(p) && !isRequiredDataProperty(p) && !fullyConstrainedDataUris.has(p.uri)
        );

        const mandatoryObjectProps = objectPropertyRestrictions.filter(isRequiredObjectPropertyRestriction);
        const fullyConstrainedUris = new Set(
            mandatoryObjectProps
                .filter(r =>
                    (r.isUniversal && r.cardinality.min >= 1) ||
                    (r.cardinality.min >= 1 && r.cardinality.max === 1)
                )
                .map(r => r.uri)
        );
        const optionalObjectProps = objectPropertyRestrictions.filter(r =>
            isOptionalObjectPropertyRestriction(r) && !fullyConstrainedUris.has(r.uri)
        );

        const hasMandatory = mandatoryDataProps.length > 0 || mandatoryObjectProps.length > 0;
        const hasOptional = optionalDataProps.length > 0 || optionalObjectProps.length > 0;

        if (!hasMandatory && !hasOptional) return null;

        const renderObjectProperty = (restriction: RestrictedObjectProperty, index: number, prefix: string) => {
            if (restriction.hasValue) {
                return (
                    <HasValueChip
                        key={`${prefix}-${index}`}
                        restriction={restriction}
                        individual={individual}
                        index={index}
                        prefix={prefix}
                        hasABoxDeclaration={this.checkHasABoxDeclaration(individual, restriction)}
                        onDeclare={this.handleDeclareOnIndividual}
                        onClear={this.handleClearABoxDeclaration}
                    />
                );
            }

            // Get ABox individuals from the knowledge graph
            const aboxIndividuals: Individual[] = this.props.knowledgeGraph
                ? this.props.knowledgeGraph.getIndividualsByClassUri(restriction.targetClassUri)
                : [];

            // Get individuals from workspace (all loaded ontologies, e.g., party:Belgium)
            const workspaceIndividuals: Individual[] = this.props.getIndividualsByClass
                ? this.props.getIndividualsByClass(restriction.targetClassUri)
                : [];

            // Combine both, avoiding duplicates by URI
            const seenUris = new Set<string>();
            const availableTargets: Individual[] = [];
            for (const ind of [...workspaceIndividuals, ...aboxIndividuals]) {
                if (!seenUris.has(ind.uri)) {
                    seenUris.add(ind.uri);
                    availableTargets.push(ind);
                }
            }

            const currentRels = this.props.knowledgeGraph
                ? Array.from(this.props.knowledgeGraph.relationships.values()).filter(
                    r => r.subject === individual && r.predicate.uri === restriction.uri
                )
                : [];

            // Get current values - the object implements Individual interface
            const currentValues: Individual[] = currentRels
                .map(rel => {
                    // The relationship object implements Individual (either ABox or TBox)
                    const obj = rel.object;
                    if ('classUris' in obj && 'isInstanceOf' in obj) {
                        return obj as Individual;
                    }
                    // Fallback: look up in workspace individuals by URI
                    const workspaceInd = this.props.getIndividualByUri?.(obj.uri);
                    if (workspaceInd) {
                        return workspaceInd;
                    }
                    // Last resort: this shouldn't happen with proper Individual implementations
                    console.warn(`Object ${obj.uri} does not implement Individual interface`);
                    return null;
                })
                .filter((ind): ind is Individual => ind !== null);

            return (
                <EditableObjectProperty
                    key={`${prefix}-${index}`}
                    restriction={restriction}
                    individual={individual}
                    availableTargets={availableTargets}
                    currentValues={currentValues}
                    index={index}
                    onValueChange={this.handleObjectPropertyValueChange}
                    onValueRemove={this.handleObjectPropertyValueRemove}
                />
            );
        };

        return (
            <>
                {hasMandatory && (
                    <>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5">Essential Properties</div>
                        {mandatoryDataProps.length > 0 && (
                            <>
                                <div className="text-[10px] text-slate-500 dark:text-white/50 mt-1.5 mb-1 italic">Data Properties</div>
                                {mandatoryDataProps.map((prop, index) => (
                                    <EditableDataProperty
                                        key={`md-${index}`}
                                        prop={prop}
                                        individual={individual}
                                        index={index}
                                        focusedInput={this.state.focusedInput}
                                        onFocusChange={this.handleFocusChange}
                                        onValueChange={this.handleDataPropertyValueChange}
                                        onRefresh={this.handleRefresh}
                                    />
                                ))}
                            </>
                        )}
                        {mandatoryObjectProps.length > 0 && (
                            <>
                                <div className="text-[10px] text-slate-500 dark:text-white/50 mt-1.5 mb-1 italic">Object Properties</div>
                                {mandatoryObjectProps.map((restriction, index) => renderObjectProperty(restriction, index, 'mo'))}
                            </>
                        )}
                    </>
                )}
                {hasOptional && (
                    <>
                        <div
                            className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5 cursor-pointer select-none"
                            onClick={() => this.setState({ optionalPropertiesExpanded: !this.state.optionalPropertiesExpanded })}
                        >
                            <span className="text-[8px]">{this.state.optionalPropertiesExpanded ? '▼' : '▶'}</span>
                            Optional Properties
                        </div>
                        {this.state.optionalPropertiesExpanded && (
                            <>
                                {optionalDataProps.length > 0 && (
                                    <>
                                        <div className="text-[10px] text-slate-500 dark:text-white/50 mt-1.5 mb-1 italic">Data Properties</div>
                                        {optionalDataProps.map((prop, index) => (
                                            <EditableDataProperty
                                                key={`od-${index}`}
                                                prop={prop}
                                                individual={individual}
                                                index={index}
                                                focusedInput={this.state.focusedInput}
                                                onFocusChange={this.handleFocusChange}
                                                onValueChange={this.handleDataPropertyValueChange}
                                                onRefresh={this.handleRefresh}
                                            />
                                        ))}
                                    </>
                                )}
                                {optionalObjectProps.length > 0 && (
                                    <>
                                        <div className="text-[10px] text-slate-500 dark:text-white/50 mt-1.5 mb-1 italic">Object Properties</div>
                                        {optionalObjectProps.map((restriction, index) => renderObjectProperty(restriction, index, 'oo'))}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </>
        );
    }

    render() {
        const modelElement = this.props.modelElement;
        if (!modelElement) {
            return (
                <div id="properties" className="text-xs">
                    <p className="text-[11px] text-slate-400 dark:text-white/40 italic">Select an element...</p>
                </div>
            );
        }

        // Check if it's an Individual interface (could be ABox or TBox)
        if (isIndividual(modelElement)) {
            const individual = modelElement;

            // Check if this individual is in the KnowledgeGraph (ABox)
            const isInKnowledgeGraph = this.props.knowledgeGraph?.getNodeByUri(individual.uri) !== undefined;

            if (isInKnowledgeGraph && individual instanceof ABoxIndividual) {
                // ABox individual - full editable display
                const schemaClass = individual.modelClass;
                const classLabel = schemaClass.label || schemaClass.name;
                const definitionGenerator = new DefinitionGenerator(this.props.knowledgeGraph);
                const generatedDefinition = definitionGenerator.generate(schemaClass, individual);
                return (
                    <div id="properties" className="text-xs" key={this.state.refreshKey}>
                        {this.state.nameEditing ? (
                            <input
                                ref={this.nameInputRef}
                                type="text"
                                defaultValue={individual.name}
                                className="w-full text-sm font-semibold text-slate-800 dark:text-white m-0 mb-1 px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 border border-accent-teal dark:border-accent/50 rounded outline-none"
                                onBlur={this.handleNameBlur}
                                onKeyDown={this.handleNameKeyDown}
                            />
                        ) : (
                            <h2
                                className="text-sm font-semibold text-slate-800 dark:text-white m-0 mb-1 cursor-pointer hover:text-accent-teal dark:hover:text-accent transition-colors duration-150 px-1.5 py-0.5 -mx-1.5 rounded hover:bg-slate-50 dark:hover:bg-white/5"
                                onClick={this.handleNameClick}
                                title="Click to rename"
                            >
                                {individual.name}
                            </h2>
                        )}
                        <div className="text-[10px] text-slate-500 dark:text-white/40 font-mono m-0 mb-1.5 truncate" title={individual.uri}>
                            {individual.uri.includes('#') ? individual.uri.split('#').pop() : individual.uri.split('/').pop()}
                        </div>
                        <div className="text-[11px] text-accent-teal dark:text-accent/80 italic m-0 mb-3">
                            &laquo;{classLabel}&raquo;
                            <span className="text-[10px] text-slate-500 dark:text-white/50 ml-1.5 not-italic">Individual</span>
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5">Definition</div>
                        <p className="text-[11px] leading-relaxed text-slate-600 dark:text-white/75 m-0 mb-2">{individual.definition || 'No definition provided'}</p>
                        {generatedDefinition && (
                            <p className="text-[11px] leading-relaxed text-[rgba(100,180,255,0.85)] italic m-0 mb-2">{generatedDefinition}</p>
                        )}
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5">Instance of Class</div>
                        <p className="text-[11px] leading-relaxed text-slate-600 dark:text-white/75 m-0 mb-2">{classLabel}</p>
                        {this.renderEssentialPropertiesForIndividual(schemaClass, individual)}
                        <div className="flex gap-2 mt-3">
                            {this.props.onExportEssentialTTL && (
                                <button
                                    className="px-2.5 py-1 text-[11px] bg-[rgba(100,180,255,0.15)] border border-[rgba(100,180,255,0.3)] rounded text-[rgba(100,180,255,0.9)] cursor-pointer"
                                    onClick={() => this.handleExportEssentialTTL(individual, schemaClass)}
                                >
                                    Essential TTL
                                </button>
                            )}
                            <button
                                className="px-2.5 py-1 text-[11px] bg-[rgba(180,180,180,0.15)] border border-[rgba(180,180,180,0.3)] rounded text-[rgba(180,180,180,0.9)] cursor-pointer"
                                onClick={this.toggleRestrictionsDebug}
                            >
                                {this.state.showRestrictionsDebug ? 'Hide' : 'Show'} Restrictions JSON
                            </button>
                        </div>
                        {this.state.showRestrictionsDebug && (
                            <div className="mt-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mb-1.5">
                                    Computed RestrictedObjectProperties
                                </div>
                                <pre className="text-[10px] leading-relaxed text-slate-600 dark:text-white/70 bg-slate-100 dark:bg-white/5 p-2 rounded overflow-auto max-h-64 whitespace-pre-wrap break-all">
                                    {this.serializeRestrictionsToJSON(computeRestrictedObjectProperties({ subClassOf: schemaClass._subClassOf }))}
                                </pre>
                            </div>
                        )}
                    </div>
                );
            } else {
                // Individual NOT in KnowledgeGraph - read-only display
                const classUris = individual.classUris;
                // Get class labels from the first classUri
                const classLabel = classUris.length > 0
                    ? classUris[0].includes('#')
                        ? classUris[0].split('#').pop()
                        : classUris[0].split('/').pop()
                    : 'Unknown';

                return (
                    <div id="properties" className="text-xs">
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-white m-0 mb-1 px-1.5 py-0.5 -mx-1.5">
                            {individual.name}
                        </h2>
                        <div className="text-[10px] text-slate-500 dark:text-white/40 font-mono m-0 mb-1.5 truncate" title={individual.uri}>
                            {individual.uri.includes('#') ? individual.uri.split('#').pop() : individual.uri.split('/').pop()}
                        </div>
                        <div className="text-[11px] text-accent-teal dark:text-accent/80 italic m-0 mb-3">
                            &laquo;{classLabel}&raquo;
                            <span className="text-[10px] text-slate-500 dark:text-white/50 ml-1.5 not-italic">Individual</span>
                        </div>

                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5">Definition</div>
                        <p className="text-[11px] leading-relaxed text-slate-600 dark:text-white/75 m-0 mb-2">
                            {individual.definition || 'No definition provided'}
                        </p>

                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/40 mt-3.5 mb-1.5 flex items-center gap-1.5">Instance of Class</div>
                        {classUris.map((classUri, idx) => (
                            <p key={idx} className="text-[11px] leading-relaxed text-slate-600 dark:text-white/75 m-0 mb-1" title={classUri}>
                                {classUri.includes('#') ? classUri.split('#').pop() : classUri.split('/').pop()}
                            </p>
                        ))}

                        <div className="mt-4 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded text-[10px] text-amber-700 dark:text-amber-400">
                            This individual is not part of the current Knowledge Graph and is read-only.
                        </div>
                    </div>
                );
            }
        }

        return null;
    }
}

export default Properties;
